import { IconButton, IconName, ActionToolbarButton } from '@optimus-web/core';
import { LocalizedText, showSuccessAlert, useDialogAction } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { TooltippedSpan } from '@mews-commander/ui';
import { PortfolioRateGroupOverview } from '@mews-commander/data-access';
import { useDeleteLocalRateGroupsMutation } from '../../../../queries';
import { DeleteLocalRateGroupDialog } from './DeleteLocalRateGroupDialog';

interface Props {
    rateGroups: PortfolioRateGroupOverview[];
    isTableBulkAction?: boolean;
    onSuccess: () => void;
}

export const DeleteLocalRateGroupContainer = ({ rateGroups, isTableBulkAction, onSuccess }: Props) => {
    const { mutateAsync } = useDeleteLocalRateGroupsMutation();

    const deleteLocalRateGroups = async () => {
        await mutateAsync(
            {
                rateGroupIds: rateGroups.map(({ id }) => id),
                enterpriseIds: rateGroups.map(({ enterpriseId }) => enterpriseId),
            },
            {
                onSuccess: () => {
                    showSuccessAlert({
                        title: (
                            <LocalizedText
                                textKey={TextKey.LocalRateGroupDeletedSuccessMessage}
                                substitutes={{ count: rateGroups.length }}
                            />
                        ),
                    });
                },
            }
        );
        onSuccess();
    };

    const deleteAction = useDialogAction(deleteLocalRateGroups);
    const isLinked = !rateGroups.length || rateGroups.some((rateGroup) => rateGroup.isPortfolioManaged);
    const hasRates = rateGroups.some((rateGroup) => !!rateGroup.rates);

    return (
        <>
            {isTableBulkAction ? (
                <ActionToolbarButton
                    icon={IconName.Delete}
                    disabled={isLinked || hasRates}
                    onClick={deleteAction.handlers.handleOpen}
                >
                    <LocalizedText textKey={TextKey.Delete} />
                </ActionToolbarButton>
            ) : (
                <TooltippedSpan
                    tooltip={
                        isLinked || hasRates ? (
                            <LocalizedText textKey={TextKey.LocalRateGroupDeleteButtonLinked} />
                        ) : null
                    }
                >
                    <IconButton
                        icon={IconName.Delete}
                        disabled={isLinked || hasRates}
                        onClick={deleteAction.handlers.handleOpen}
                        type="submit"
                    />
                </TooltippedSpan>
            )}
            {deleteAction.states.isDialogOpened && (
                <DeleteLocalRateGroupDialog
                    rateGroups={rateGroups}
                    isLoading={deleteAction.states.isProcessing}
                    onCancel={deleteAction.handlers.handleCancel}
                    onConfirm={deleteAction.handlers.handleConfirm}
                />
            )}
        </>
    );
};
